/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';

import { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import root from 'window-or-global';
import Quantity from '@/components/AZCustomComponent/QuantityComponent';
import { Label } from '@/features/i18n';
import { useLabel } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { cartConstants } from '@/constants/cart';
import azCommonStyles from '@/theme/globals.module.scss';
import { usePatchOrderItem } from '@/features/orders/api/patchOrderItem';
import type { ItemQohErrorMessage } from '@/utils/itemHelpers/types';
import { mergeDefaultWithState } from '@/utils/itemHelpers/fulfillmentHelpers/mergeDefaultWithState';
import { getMaxAvailQtyFromOptions } from '@/utils/itemHelpers/fulfillmentHelpers/getMaxAvailQtyFromOptions';
import { createItemQoHErrorMessage } from '@/utils/itemHelpers/fulfillmentHelpers/createItemQoHErrorMessage';
import { calculateNewFulfillmentProps } from '@/utils/itemHelpers/fulfillmentHelpers/calculateNewFulfillmentProps';
import { getItemFulfillmentoptionsFromGroups } from '@/utils/itemHelpers/fulfillmentHelpers/getItemFulfillmentoptionsFromGroups';
import styles from '@/components/Cart/CommonCounterPart/InOrderQuantitySelector/styles.module.scss';
import type { UpdateQuantityQoHError } from '@/types/analytics';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { createFilteredFulfillmentAvailableAtSelectedQty } from '@/utils/analytics/legacyUtils/createFilteredFulfillmentAvailableAtSelectedQty';
import { createFulfillmentAvailableAtSelectedQtyMessage } from '@/utils/analytics/legacyUtils/createFulfillmentAvailableAtSelectedQtyMessage';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useQueryClient } from '@tanstack/react-query';
import {
  getProductSkuDetailsKey,
  getProductSkuDetailsFromCache,
} from '@/features/product/api/getProductSkuDetails';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import type { FulfillmentGroup } from '@/types/reduxStore/skuDetails';
import { type getAllAvailableQuantitiesForSku } from '@/utils/getFulfillmentOption';
import { useProductList } from '@/features/shelf/context/useProductList';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useFeatureFlag } from '@/features/globalConfig';
import { useOMSFlags } from '@/features/product/utils/getOMSFlags';
import { useGCPPriceFlags } from '@/features/product/utils/getGCPPriceFlags';
import { formatPrice } from '@/utils/validator/formatPrice';
import { useAvailabilityInfoData } from '@/features/orders/api/getAvailabilityInfo';
import { CalmBoxStepper } from '@/features/cart/components/QuantityStepper';

const qohErrorTypes = {
  cartQoH: 'Cart QOH',
};

const trackUpdateQuantityQoHError = (
  fulfillmentGroups: FulfillmentGroup[],
  lineItem: Props['data'],
  requestedQuantity: number,
  itemQohErrorMessage: ItemQohErrorMessage,
) => {
  const { pageName = '', pageType = '', siteSection = '' } = root.utag_data || {};
  const itemFulfillmentOptions = getItemFulfillmentoptionsFromGroups(fulfillmentGroups);
  const fulfillmentOptionsUpdated = calculateNewFulfillmentProps(
    itemFulfillmentOptions,
    Number(requestedQuantity),
  );
  const finalOptionsResult = mergeDefaultWithState(fulfillmentOptionsUpdated);
  const fulfillmentAvailableAtSelectedQty =
    createFilteredFulfillmentAvailableAtSelectedQty(
      finalOptionsResult,
      itemQohErrorMessage?.optionsWithError,
    ) ?? '';
  const fulfillmentAvailableAtSelectedQtyMessage =
    createFulfillmentAvailableAtSelectedQtyMessage(finalOptionsResult, itemQohErrorMessage) ?? '';
  const productAvailableQuantity = getMaxAvailQtyFromOptions(finalOptionsResult).toString() ?? '';
  const brand = lineItem?.productInfo?.brand ?? '';
  const productId = lineItem?.productInfo?.skuId ?? '';
  const productCorePrice = lineItem?.lineItemPriceInfo?.corePrice?.toString() ?? '';
  const productDiscountPrice = lineItem?.lineItemPriceInfo?.dealAppliedAmount?.toString() ?? '';
  const productFullfillmentType = lineItem?.selectedFulfillmentType?.toString() ?? '';
  const productRequestedQuantity = requestedQuantity?.toString() ?? '';
  const clickData: UpdateQuantityQoHError = {
    eventType: 'updateQuantity',
    pageType: pageType,
    pageName: pageName,
    siteSection: siteSection,
    fulfillmentAvailableAtSelectedQtyList: [fulfillmentAvailableAtSelectedQty],
    fulfillmentAvailableAtSelectedQtyMessage: [fulfillmentAvailableAtSelectedQtyMessage],
    productAvailableQuantityList: [productAvailableQuantity],
    productBrandList: [brand],
    productCorePriceList: [productCorePrice],
    productDiscountPriceList: [productDiscountPrice],
    productFullfillmentTypeList: [productFullfillmentType],
    productIdList: [productId],
    productPartList: [],
    productRequestedQuantityList: [productRequestedQuantity],
    qohErrorType: [qohErrorTypes.cartQoH],
  };
  clickTrack(clickData);
};

type Props = {
  variant: 'classic' | 'calm';
  className?: string;
  data: {
    itemQuantity: string | number;
    dealApplied?: boolean;
    lineItemId?: string;
    orderType?: string;
    unitAmount?: number;
    productInfo: {
      skuId?: string;
      productId?: string;
      brand?: string;
    };
    lineItemPriceInfo?: {
      corePrice: number;
      dealAppliedAmount: number;
      unitPrice: number;
      listPrice: number;
    };
    selectedFulfillmentType?: number | null;
  };
  isMini: boolean;
  label_shipping_NotAvailableForSelectedQty?: string;
  fulfillmentOptions?: FulfillmentGroup[];
  showQuantityError?: (itemQohErrorMessage: ItemQohErrorMessage) => void;
  updatedValue?: (val: number) => void;
  updateQuantityError?: (errorMsg: boolean) => void;
  updateQuantityErrorItem?: (errorFlag: boolean, errorNdd?: boolean) => void;
  handleQuantityChange?: (
    quantity: number,
    hasExceededAvailableQuantity: boolean,
    showQuantityWarningMessage?: boolean,
  ) => void;
  updateTotalQuantity?: (newQuantity: number, updatedOnMount?: boolean) => void;
  pageType?: string;
  updateIsLoading?: (isLoading: boolean) => void;
  updateRequestingOrderItem?: () => void;
  disableRemoveButton?: boolean;
  coreDeposit?: number;
  isCart?: boolean;
  readyStatus?: string;
  fulFillmentId?: number;
  skuAvailableQuantities: ReturnType<typeof getAllAvailableQuantitiesForSku>;
  isNewMessagingShown?: boolean;
  isDiscountedStrikeThroughPrice?: boolean;
  removePart?: () => Promise<void>;
  productName: string;
  onExceededMaxAttempt?: (exceeded: boolean) => void;
};

/**
 * InOrderQuantitySelector - Quantity selector component for cart and product detail pages
 *
 * Renders a quantity stepper (increment/decrement buttons) that handles:
 * - Item quantity changes with real-time inventory validation
 * - Fulfillment type switching (BOPUS, Next-Day, Ship-to-Home, Same-Day)
 * - Maximum quantity limits per fulfillment method
 * - Error messaging for quantity exceeded scenarios
 *
 * Increment button disable strategies :
 * - Reactive: Disables increment when no inventory exists OR after user has already attempted to exceed the limit
 */
export const InOrderQuantitySelector = (props: Props) => {
  const [count, setCount] = useState(parseInt((props.data.itemQuantity as string) || '1'));
  const [hasExceededAvailableQuantity, setHasExceededAvailableQuantity] = useState(false);
  const [isIncOrDecBtnTouched, setIsIncOrDecBtnTouched] = useState(false);
  const rootClass = props.className
    ? `${styles.counterpart} ${props.className}`
    : styles.counterpart;
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const labelQuantityNotAvailable = useLabel('label_shipping_NotAvailableForSelectedQty');
  const isNewDealsApiEnabled = useFeatureFlag('IS_NEW_DEALS_API_ENABLED') === 'true';
  const { useOMSInventoryQohService } = useOMSFlags();
  const { useGCPPriceService } = useGCPPriceFlags();
  const labelAnd = useLabel('labels_order_and');
  const queryClient = useQueryClient();
  const router = useLegacyRouter();
  const { data: headerData } = useHeaderData();
  const locale = useLocale();
  const reloadCartPage = useFeatureFlag('RELOAD_CART_PAGE') === 'true';
  const { refetch: refetchAvailabilityInfo } = useAvailabilityInfoData();

  const onAddToCartSuccess = () => {
    const skuIds: string[] = [];
    const index =
      shelfData?.shelfParts?.findIndex(
        (item) => item.skuNumber.toString() === data?.productInfo?.skuId,
      ) ?? 0;
    // since there are two productSkuDetails queries we must pick the first or second half depending on its index
    shelfData?.shelfParts
      ?.slice(index >= 12 ? 12 : 0, index >= 12 ? undefined : 12)
      .forEach((part) => {
        skuIds.push(part.skuNumber.toString());
      });

    if (isPDP && productDetails?.product?.itemId) {
      skuIds.push(productDetails.product.itemId);
    }
    if (props.isCart) {
      skuIds.push(props.data.productInfo.skuId!);
    }

    if (skuIds && skuIds.length > 0) {
      const skuDetailsQueryOptions = {
        skuIds: skuIds,
        vehicleId: catalogVehicleId,
        storeNumber,
        isNewDealsApiEnabled,
        useOMSInventoryQohService,
        useGCPPriceService,
      };
      const skuDetails = getProductSkuDetailsFromCache(queryClient, skuDetailsQueryOptions);
      const updatedSkuDetails = skuDetails?.map(
        (skuDetailItem: {
          skuPricingAndAvailability: { productId: string | undefined; skuId: string | undefined };
        }) => {
          if (skuDetailItem.skuPricingAndAvailability?.skuId === data?.productInfo?.skuId) {
            return {
              ...skuDetailItem,
              quantityInCart: count,
            };
          }
          return skuDetailItem;
        },
      );
      const skuDetailsKey = getProductSkuDetailsKey(skuDetailsQueryOptions);
      queryClient.setQueryData(skuDetailsKey, updatedSkuDetails);
    }
  };

  const onErrorCode = (errorCode: number | undefined, errorStatus: string | undefined) => {
    if (
      reloadCartPage &&
      (errorCode === 403 || (errorCode === 400 && (errorStatus === '7033' || '7025')))
    ) {
      window.location.reload();
    }
  };

  const { mutateAsync: patchOrderItem } = usePatchOrderItem({
    onAddToCartSuccess,
    onErrorCode,
  });
  const throttle = (
    fn: (...args: [number, boolean, number | undefined]) => void,
    waitTime: number,
    immediate?: boolean,
  ) => {
    return (...args: [number, boolean, number | undefined]) => {
      if (timeout.current !== null) {
        clearTimeout(timeout.current);
      }
      timeout.current = setTimeout(() => {
        if (!immediate) {
          fn.apply(this, [...args]);
        }
      }, waitTime);

      if (immediate && !timeout) {
        fn.apply(this, [...args]);
      }
    };
  };

  let storeOrderMaxQty = 0;
  let onlineOrderMaxQty = 0;
  props.fulfillmentOptions?.forEach((fulfillment) => {
    if (fulfillment.groupId === FULFILLMENT_METHODS.STOREORDER) {
      storeOrderMaxQty = fulfillment.maxAvailableQty;
    } else if (fulfillment.groupId === FULFILLMENT_METHODS.ONLINEORDER) {
      onlineOrderMaxQty = fulfillment.maxAvailableQty > 99 ? 99 : fulfillment.maxAvailableQty;
    }
  });

  const selectedGroupAvailableQuantity = (
    newFulfillmentId = props.data.selectedFulfillmentType,
  ) => {
    switch (true) {
      case newFulfillmentId === FULFILLMENT_METHODS.SAMEDAY:
        return props.skuAvailableQuantities?.sddQuantity;
      case newFulfillmentId === FULFILLMENT_METHODS.NEXTDAY:
        if (
          props.skuAvailableQuantities?.nddQuantity !== undefined &&
          props?.data?.itemQuantity !== undefined &&
          (props?.data?.itemQuantity as number) > props.skuAvailableQuantities?.nddQuantity
        ) {
          return props.skuAvailableQuantities?.sthQuantity;
        } else {
          return props.skuAvailableQuantities?.nddQuantity;
        }
      case newFulfillmentId === FULFILLMENT_METHODS.ONLINEORDER:
        return props.skuAvailableQuantities?.sthQuantity;
      case newFulfillmentId === FULFILLMENT_METHODS.STOREORDER:
        return props.skuAvailableQuantities?.bopusQuantity;
    }
  };

  useEffect(() => {
    props.updateTotalQuantity?.(count, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCount(props.data.itemQuantity as number);
  }, [props.data.itemQuantity]);

  useEffect(() => {
    const availableQtyforselectedFulfillment =
      selectedGroupAvailableQuantity() === undefined ? false : true;
    if (availableQtyforselectedFulfillment) {
      onLoadQuantityErrorCheckout();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeOrderMaxQty, onlineOrderMaxQty, props.skuAvailableQuantities]);

  const { fulFillmentId, onExceededMaxAttempt } = props;
  useEffect(() => {
    setHasExceededAvailableQuantity(false);
    onExceededMaxAttempt?.(false);
  }, [fulFillmentId, onExceededMaxAttempt]);

  const onLoadQuantityErrorCheckout = () => {
    const { isMini, data, updateQuantityErrorItem } = props;
    const { itemQuantity } = data;
    if (!isMini) {
      const isQuantityError =
        itemQuantity !== undefined && (itemQuantity as number) > selectedGroupAvailableQuantity()!;

      // Check if this is specifically an NDD error
      // NDD error occurs when: item quantity >= NDD available quantity AND NDD is available (> 0)
      // This applies regardless of current fulfillment method since it may have auto-switched
      const isNddError =
        itemQuantity !== undefined &&
        (props.skuAvailableQuantities?.nddQuantity ?? 0) > 0 &&
        (itemQuantity as number) >= (props.skuAvailableQuantities?.nddQuantity ?? 0);

      updateQuantityErrorItem?.(isQuantityError, isNddError);
    }
  };

  const maxQuantityCheck = (newFulfillmentId?: number) => {
    const maxQuantityV2 = selectedGroupAvailableQuantity(newFulfillmentId)!;
    return Math.min(maxQuantityV2, 99);
  };

  const quantityErrorCheck = (checkQuantity: boolean, quantity: number) => {
    const { isMini, updateQuantityError, updateQuantityErrorItem, updatedValue } = props;

    if (!isMini && quantity > 0) {
      // Check if this is specifically an NDD error (independent of checkQuantity)
      // NDD error occurs when: quantity >= NDD available AND NDD is available (> 0)
      // This applies regardless of current fulfillment method since it may have auto-switched
      const isNddError =
        (props.skuAvailableQuantities?.nddQuantity ?? 0) > 0 &&
        quantity >= (props.skuAvailableQuantities?.nddQuantity ?? 0);

      updateQuantityError?.(checkQuantity);
      updateQuantityErrorItem?.(checkQuantity, isNddError);
    } else if (count) {
      updatedValue?.(count);
    }
  };

  const calculateItemOoHError = (quantity: number) => {
    const { data: lineItem, showQuantityError, fulfillmentOptions: fulfillmentGroups } = props;
    const noError: ItemQohErrorMessage = {
      errorMessage: '',
      optionsWithError: [],
    };
    const itemQohErrorMessage = createItemQoHErrorMessage(
      fulfillmentGroups!,
      quantity,
      labelAnd,
      labelQuantityNotAvailable,
    );

    if (!!itemQohErrorMessage && itemQohErrorMessage.errorMessage.length > 0) {
      trackUpdateQuantityQoHError(fulfillmentGroups!, lineItem, quantity, itemQohErrorMessage);
      showQuantityError?.(itemQohErrorMessage);
    } else {
      showQuantityError?.(noError);
    }
  };

  const handleChange = (quantity: number) => {
    let showQuantityWarningMessage = true;
    const { handleQuantityChange, showQuantityError, fulFillmentId } = props;

    if (quantity === count) {
      return;
    }

    const nddQuantity = props.skuAvailableQuantities?.nddQuantity ?? 0;
    const sthQuantity = props.skuAvailableQuantities?.sthQuantity ?? 0;
    const isNddAvailable = nddQuantity > 0;
    let maxQuantity = null;
    let fulfillmentIdChanged = false;
    let newFulfillmentId;
    if (
      fulFillmentId === FULFILLMENT_METHODS.NEXTDAY &&
      nddQuantity < quantity &&
      sthQuantity > nddQuantity
    ) {
      fulfillmentIdChanged = true;
      newFulfillmentId = FULFILLMENT_METHODS.ONLINEORDER;
      maxQuantity = Math.min(maxQuantityCheck(FULFILLMENT_METHODS.ONLINEORDER), 99);
    } else if (
      quantity > 0 &&
      isNddAvailable &&
      fulFillmentId === FULFILLMENT_METHODS.ONLINEORDER &&
      nddQuantity >= quantity
    ) {
      fulfillmentIdChanged = true;
      newFulfillmentId = FULFILLMENT_METHODS.NEXTDAY;
      maxQuantity = Math.min(maxQuantityCheck(FULFILLMENT_METHODS.NEXTDAY), 99);
    } else {
      maxQuantity = Math.min(maxQuantityCheck(), 99);
    }

    const maxQuantityReached = quantity > maxQuantity;
    const isDecrement = quantity < count;
    setIsIncOrDecBtnTouched(true);
    const adjustedQuantityForNegative = quantity < 0 ? count : quantity;
    const newQuantity = maxQuantityReached ? maxQuantity : adjustedQuantityForNegative;
    if (!props.isMini && showQuantityError) {
      calculateItemOoHError(newQuantity);
    }
    // Check if this is an NDD quantity reached/exceeded scenario (for messaging only)
    const isNddQuantityReached =
      (props.skuAvailableQuantities?.nddQuantity ?? 0) > 0 &&
      quantity >= (props.skuAvailableQuantities?.nddQuantity ?? 0);

    // Only consider it a true "exceeded" scenario if max available quantity is reached
    if (maxQuantityReached) {
      handleQuantityChange?.(count, false, showQuantityWarningMessage);
      setHasExceededAvailableQuantity(true);
      props.onExceededMaxAttempt?.(true);

      if (newQuantity !== count) {
        updateQuantity(newQuantity, fulfillmentIdChanged, newFulfillmentId);
      }
    } else {
      setHasExceededAvailableQuantity(false);
      // Show NDD warning message even if not at max quantity limit
      if (isNddQuantityReached) {
        handleQuantityChange?.(count, false, true);
      } else if (count) {
        showQuantityWarningMessage = false;
        handleQuantityChange?.(count, false, showQuantityWarningMessage);
      }

      if (isDecrement) {
        props.onExceededMaxAttempt?.(false);
      }

      quantityErrorCheck(false, quantity);
      updateQuantity(newQuantity, fulfillmentIdChanged, newFulfillmentId);
    }
  };

  const updateQuantity = (
    newQuantity: number,
    fulfillmentIdChanged = false,
    newFulfillmentId?: number,
  ) => {
    const { updateTotalQuantity } = props;

    if (props.isMini) {
      props.updatedValue?.(newQuantity);
    }
    setCount(newQuantity > 0 ? newQuantity : 0);
    updateQuantityDataWrapper(
      newQuantity > 0 ? newQuantity : 0,
      fulfillmentIdChanged,
      newFulfillmentId,
    );
    updateTotalQuantity?.(newQuantity > 0 ? newQuantity : 0);
  };

  const catalogVehicleId = headerData?.vehicleMap?.catalogVehicleId ?? '';
  const { data: storeDetailsData } = useStoreDetailsData();
  const isBopusEnabled = storeDetailsData?.bopusEnabled;
  const storeNumber = storeDetailsData?.storeNumber;
  const isBopusMexicoStoreEnabled = locale === countryCodes.mx && isBopusEnabled;
  const isPDP = router.pathname === '/products/[...slug]';

  const { data: shelfData } = useProductList();
  const { data: productDetails } = useProductDetails({ enabled: isPDP });

  const updateQuantityDataWrapper = throttle(
    async (newQuantity: number, fulfillmentIdChanged: boolean, newFulfillmentId?: number) => {
      const { updateIsLoading, data, updateRequestingOrderItem } = props;

      const fulfillmentId = fulfillmentIdChanged ? newFulfillmentId : props.fulFillmentId;

      if (newQuantity > 0) {
        if (!props.disableRemoveButton) {
          updateRequestingOrderItem?.();
        }
        if (updateIsLoading) {
          updateIsLoading(true);
        }
        try {
          await patchOrderItem({
            skuId: data.productInfo.skuId!,
            productId: data.productInfo.productId,
            quantity: newQuantity.toString(),
            vehicleId: catalogVehicleId,
            storeNumber,
            fulfillmentTypeId: fulfillmentId,
            commerceItemId: data.lineItemId,
          });
          void refetchAvailabilityInfo();
          /*
           Note: we should not refetch the header here since
           that would wipe out order details in redux.
         */
          if (updateIsLoading) {
            updateIsLoading(false);
          }
        } catch {
          if (updateIsLoading) {
            updateIsLoading(false);
          }
        }
      }
    },
    1000,
  );

  const renderMiniCartError = (disableAddToCartBtn: boolean) => {
    const returnValue = null;

    props.handleQuantityChange?.(count, disableAddToCartBtn);
    if (props.isMini) {
      props.updatedValue?.(count);
    }
    return returnValue;
  };

  const getUnitPrice = () => {
    const { data, isMini, isDiscountedStrikeThroughPrice } = props;
    let amt: number | undefined;

    if (isDiscountedStrikeThroughPrice && data.dealApplied) {
      amt = data?.lineItemPriceInfo?.unitPrice;
    } else {
      amt = isMini ? (data?.unitAmount ?? 0) : (data?.lineItemPriceInfo?.listPrice ?? 0);
    }
    return amt;
  };

  const renderEachItemPrice = () => {
    const { isCart } = props;
    const amt = getUnitPrice();
    let returnValue = null;
    const cartWrapper = isCart ? styles.cartClass : '';

    const eachLabel = isBopusMexicoStoreEnabled ? (
      <Label label="label_cart_Each" />
    ) : (
      <Label label="label_cart_lineItem_Each" />
    );

    if (count > 1 && amt) {
      returnValue = (
        <div className={isCart ? (isBopusMexicoStoreEnabled ? styles.mxCart : cartWrapper) : ''}>
          <div
            className={cx(
              azCommonStyles['az-body-3-regular'],
              azCommonStyles['az-margin-top-4xs'],
              azCommonStyles['az-align-center'],
              styles.unitPrice,
            )}
          >
            {eachLabel} ${formatPrice(parseFloat(amt as unknown as string))}
          </div>
        </div>
      );
    }

    return returnValue;
  };

  const { data, pageType, removePart, isNewMessagingShown = false } = props;

  const extraMargin = pageType === cartConstants.productDetails;
  let disableAddToCartBtn = false;

  if (data.orderType === cartConstants.STOREORDER) {
    disableAddToCartBtn = count > storeOrderMaxQty;
  } else if (data.orderType === cartConstants.ONLINEORDER) {
    disableAddToCartBtn = count > onlineOrderMaxQty;
  }

  const selectedFulfillmentGroup = props.fulfillmentOptions?.find((option) => option.selected);
  const selectedFulfillmentOption = selectedFulfillmentGroup?.groupFulfillmentOptions?.find(
    (option) => option.fulfillmentTypeId === props.fulFillmentId || option.selected,
  );
  const isAvailable =
    selectedFulfillmentOption?.available ||
    (props.fulFillmentId === FULFILLMENT_METHODS.STOREORDER &&
      selectedFulfillmentOption?.sisterStoreAvailable);

  const hasNoInventoryAvailable = maxQuantityCheck() <= 0;
  const disableReactively = hasNoInventoryAvailable || hasExceededAvailableQuantity;

  return (
    <div className={rootClass} data-testid="common-counter-v2">
      {!isNewMessagingShown && renderMiniCartError(disableAddToCartBtn)}
      <div className={azCommonStyles['az-align-center']}>
        {props.variant === 'calm' ? (
          <CalmBoxStepper
            onInputChange={handleChange}
            onIncrementDecrement={handleChange}
            count={count}
            minQuantity={1}
            disableIncrementBtn={disableReactively}
            hasError={!isNewMessagingShown && hasExceededAvailableQuantity}
            maxQuantity={maxQuantityCheck(props.fulFillmentId)}
            handleZeroInput={removePart}
            disabled={!isAvailable}
            productName={props.productName}
            isMaxQtyReached={hasExceededAvailableQuantity}
            unitPrice={
              getUnitPrice()
                ? formatPrice(parseFloat(getUnitPrice() as unknown as string))
                : undefined
            }
            hideMaxQuantityError={isNewMessagingShown}
            allow0Input
          />
        ) : (
          <>
            <Quantity
              onInputChange={handleChange}
              onIncrementDecrement={handleChange}
              count={count}
              minQuantity={1}
              disableIncrementBtn={disableReactively}
              hasError={!isNewMessagingShown && hasExceededAvailableQuantity}
              maxQuantity={maxQuantityCheck(props.fulFillmentId)}
              moreMargin={extraMargin}
              isIncOrDecBtnTouched={isIncOrDecBtnTouched}
              handleZeroInput={removePart}
              disabled={!isAvailable}
              productName={props.productName}
            />
            {renderEachItemPrice()}
          </>
        )}
      </div>
    </div>
  );
};
